document.documentElement.classList.add('js');

const ptcMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

function initPtcReveal(scope = document) {
  const items = scope.querySelectorAll('.ptc-reveal:not([data-ptc-reveal-ready])');

  if (ptcMotionQuery.matches || !('IntersectionObserver' in window)) {
    items.forEach((item) => {
      item.dataset.ptcRevealReady = 'true';
      item.classList.add('is-visible');
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  items.forEach((item) => {
    item.dataset.ptcRevealReady = 'true';
    observer.observe(item);
  });
}

function initPtcVideos(scope = document) {
  scope.querySelectorAll('.ptc-hero__video:not([data-ptc-video-ready]), .ptc-story__video:not([data-ptc-video-ready])').forEach((video) => {
    video.dataset.ptcVideoReady = 'true';
    if (ptcMotionQuery.matches) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
  });
}

function initPtcProof(scope = document) {
  scope.querySelectorAll('.ptc-proof:not([data-ptc-proof-ready])').forEach((section) => {
    const grid = section.querySelector('[data-ptc-proof-grid]');
    const cards = [...section.querySelectorAll('[data-ptc-proof-card]')];
    const previous = section.querySelector('[data-ptc-proof-prev]');
    const next = section.querySelector('[data-ptc-proof-next]');
    const status = section.querySelector('[data-ptc-proof-status]');
    if (!grid || cards.length < 2 || !previous || !next) return;

    section.dataset.ptcProofReady = 'true';
    let activeIndex = 0;
    let ticking = false;

    const updateControls = () => {
      previous.disabled = activeIndex === 0;
      next.disabled = activeIndex === cards.length - 1;
      if (status) status.textContent = `${activeIndex + 1} / ${cards.length}`;
    };

    const getCardLeft = (card) => {
      return card.getBoundingClientRect().left - grid.getBoundingClientRect().left + grid.scrollLeft;
    };

    const goToCard = (index) => {
      activeIndex = Math.max(0, Math.min(cards.length - 1, index));
      grid.scrollTo({ left: getCardLeft(cards[activeIndex]), behavior: ptcMotionQuery.matches ? 'auto' : 'smooth' });
      updateControls();
    };

    previous.addEventListener('click', () => goToCard(activeIndex - 1));
    next.addEventListener('click', () => goToCard(activeIndex + 1));

    grid.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        activeIndex = cards.reduce((closestIndex, card, index) => {
          const currentDistance = Math.abs(getCardLeft(card) - grid.scrollLeft);
          const closestDistance = Math.abs(getCardLeft(cards[closestIndex]) - grid.scrollLeft);
          return currentDistance < closestDistance ? index : closestIndex;
        }, 0);
        updateControls();
        ticking = false;
      });
    }, { passive: true });

    updateControls();
  });
}

function initPtcProductRotators(scope = document) {
  scope.querySelectorAll('[data-ptc-card-rotator]:not([data-ptc-card-rotator-ready])').forEach((rotator) => {
    const images = [...rotator.querySelectorAll('.ptc-product-card__image--rotating')];
    if (images.length === 0) return;

    rotator.dataset.ptcCardRotatorReady = 'true';
    let activeIndex = 0;
    let timerId = null;
    const requestedDelay = Number.parseInt(rotator.dataset.ptcRotateMs, 10);
    const delay = Number.isFinite(requestedDelay) ? Math.max(1000, requestedDelay) : 4000;

    const showImage = (index) => {
      activeIndex = index;
      images.forEach((image, imageIndex) => {
        image.classList.toggle('is-active', imageIndex === activeIndex);
      });
    };

    const stop = () => {
      if (timerId === null) return;
      window.clearInterval(timerId);
      timerId = null;
    };

    const advance = () => {
      if (!rotator.isConnected) {
        stop();
        return;
      }
      if (document.hidden) return;
      showImage((activeIndex + 1) % images.length);
    };

    const start = () => {
      if (timerId !== null || images.length < 2 || ptcMotionQuery.matches) return;
      timerId = window.setInterval(advance, delay);
    };

    showImage(0);

    if (ptcMotionQuery.matches || images.length < 2) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              start();
            } else {
              stop();
            }
          });
        },
        { rootMargin: '120px 0px', threshold: 0.05 }
      );
      observer.observe(rotator);
    } else {
      start();
    }
  });
}

function initPtc(scope = document) {
  initPtcReveal(scope);
  initPtcVideos(scope);
  initPtcProof(scope);
  initPtcProductRotators(scope);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPtc());
} else {
  initPtc();
}

document.addEventListener('shopify:section:load', (event) => initPtc(event.target));
